import { Calendar, Clock, MapPin, MoreHorizontal, Play, Settings, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import type * as Types from '../../../graphql/types';
import {
  useGetHotelTeamForRecipientsQuery,
  useGetUserDropdownOptionsQuery,
} from '../gql/_gen_/report-subscriptions.gql';
import { DELIVERY_TIME_OPTIONS, TIMEZONE_OPTIONS } from '../hooks/use-subscription-management';
import { formatSelectedDays } from '../utils';

interface ReportSubscriptionCardProps {
  subscription: Types.ReportSubscription | null;
  onToggleActive?: (id: string, isActive: boolean) => void;
  onEditPreferences?: (id: string) => void;
  onDelete?: (id: string) => void;
  onRunNow?: (id: string) => void;
  loading?: boolean;
  canManage?: boolean;
  canRunNow?: boolean;
}

export const ReportSubscriptionCard: React.FC<ReportSubscriptionCardProps> = ({
  subscription,
  onToggleActive,
  onEditPreferences,
  onDelete,
  onRunNow,
  loading = false,
  canManage = true,
  canRunNow = false,
}) => {
  const { data: userOptionsData } = useGetUserDropdownOptionsQuery();
  const { data: hotelTeamData } = useGetHotelTeamForRecipientsQuery({
    variables: {
      brandCode: subscription?.brandCode ?? '',
    },
    skip: !subscription?.brandCode,
  });

  if (!subscription) {
    return (
      <Card className='w-full'>
        <CardHeader>
          <CardTitle className='text-lg font-medium'>Loading...</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='animate-pulse space-y-4'>
            <div className='h-4 bg-gray-300 rounded w-3/4'></div>
            <div className='h-4 bg-gray-300 rounded w-1/2'></div>
            <div className='h-4 bg-gray-300 rounded w-5/6'></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const timezoneLabel =
    TIMEZONE_OPTIONS.find((tz) => tz.value === subscription.timezone)?.label ||
    subscription.timezone;
  const deliveryTimeLabel =
    DELIVERY_TIME_OPTIONS.find((time) => time.value === subscription.preferences.deliveryTime)
      ?.label || subscription.preferences.deliveryTime;

  // Use shared utility for formatting days
  const formattedDays = formatSelectedDays(subscription.preferences);

  const rmUser = userOptionsData?.userDropdownOptions?.find((u) => u?.id === subscription.rmUserId);
  const rmUserLabel = rmUser?.name || rmUser?.email || String(subscription.rmUserId);

  const hotelTeamMembers = (hotelTeamData?.getHotelTeam ?? []).filter(
    (member): member is NonNullable<typeof member> & { id: string } => !!member?.id,
  );

  const recipients = (subscription.preferences.recipientTeamMemberIds ?? [])
    .map((id) => hotelTeamMembers.find((member) => member.id === id))
    .filter((member): member is NonNullable<typeof member> => !!member);

  const recipientsText = recipients
    .map((member) =>
      member.name ? `${member.name}${member.email ? ` (${member.email})` : ''}` : member.email,
    )
    .filter((text): text is string => !!text)
    .join(', ');

  const handleToggleActive = (checked: boolean) => {
    onToggleActive?.(subscription.id, checked);
  };

  const handleEditPreferences = () => {
    onEditPreferences?.(subscription.id);
  };

  const handleDelete = () => {
    onDelete?.(subscription.id);
  };

  const handleRunNow = () => {
    onRunNow?.(subscription.id);
  };

  return (
    <Card className='w-full'>
      <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
        <div className='flex-1'>
          <CardTitle className='text-lg font-medium'>
            {subscription.hotel?.name || `Brand ${subscription.brandCode}`}
          </CardTitle>
          <CardDescription className='text-sm text-muted-foreground'>
            {subscription.hotel?.brand_code} • {subscription.brandCode}
          </CardDescription>
        </div>
        <div className='flex items-center space-x-2'>
          {canRunNow && (
            <Button
              variant='outline'
              size='sm'
              className='h-8'
              onClick={handleRunNow}
              disabled={loading || !subscription.isActive}
              title='Manually trigger report generation'
            >
              <Play className='mr-2 h-4 w-4' />
              Run now
            </Button>
          )}
          <Switch
            checked={subscription.isActive}
            onCheckedChange={handleToggleActive}
            disabled={loading || !canManage}
          />
          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' size='sm' className='h-8 w-8 p-0' disabled={loading}>
                  <MoreHorizontal className='h-4 w-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem onClick={handleEditPreferences}>
                  <Settings className='mr-2 h-4 w-4' />
                  Edit Preferences
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDelete} className='text-red-600'>
                  <Trash2 className='mr-2 h-4 w-4' />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className='grid grid-cols-1 gap-3'>
          {/* Status Badge and ID */}
          <div className='flex items-center space-x-2'>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                subscription.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}
            >
              {subscription.isActive ? 'Active' : 'Inactive'}
            </span>
            <span className='text-xs text-muted-foreground'>ID: {subscription.id}</span>
          </div>

          {/* Days */}
          <div className='flex items-center space-x-2 text-sm'>
            <Calendar className='h-4 w-4 text-muted-foreground' />
            <span className='text-muted-foreground'>Days:</span>
            <span>{formattedDays}</span>
          </div>

          {/* Description */}
          {subscription.preferences.description && (
            <div className='flex items-center space-x-2 text-sm'>
              <span className='text-muted-foreground'>Description:</span>
              <span className='truncate'>{subscription.preferences.description}</span>
            </div>
          )}

          {/* Delivery Time */}
          <div className='flex items-center space-x-2 text-sm'>
            <Clock className='h-4 w-4 text-muted-foreground' />
            <span className='text-muted-foreground'>Time:</span>
            <span>{deliveryTimeLabel}</span>
          </div>

          {/* Timezone */}
          <div className='flex items-center space-x-2 text-sm'>
            <MapPin className='h-4 w-4 text-muted-foreground' />
            <span className='text-muted-foreground'>Timezone:</span>
            <span>{timezoneLabel}</span>
          </div>

          {/* User */}
          <div className='flex items-center space-x-2 text-sm'>
            <span className='text-muted-foreground'>Revenue Manager:</span>
            <span className='truncate'>{rmUserLabel}</span>
          </div>

          {/* Recipients */}
          {recipientsText && (
            <div className='flex items-center space-x-2 text-sm'>
              <span className='text-muted-foreground'>Recipients:</span>
              <span className='truncate'>{recipientsText}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
